// Extracted from pages/Articles/CreateArticle.js (behavior-preserving move).
// Functions are recreated on every render with the latest wizard state, exactly as
// they were when they lived inside the page component. `log` is the original no-op.
import { flushSync } from 'react-dom';
import { directusAPI } from '../../../api';
import { saveArticleToDirectus } from '../../../pages/Articles/saveArticle';
import { parseMarkdownToEditorJS } from '../lib/markdownToEditorJS';
import { LOCAL_WORKER_API, isLocalhost } from '../lib/workerEndpoints';
import { STEP_INDEX_TO_NAME as stepIndexToName } from '../lib/wizardSteps';
import {
  createQueueAPI,
  getStoredToken,
  isApiServerAvailable,
  log,
  markArticleFailed,
  requestDraftFromWorkers,
} from '../services/articleGenerationApi';

/**
 * Draft generation (step 2): creates/updates the Directus record, then generates the
 * draft through the worker API, falling back to the Directus queue when available.
 */
export function useDraftGeneration(ctx) {
  const {
    navigate,
    searchParams,
    getValidStepNumber,
    editorRef,
    articleContextRef,
    sourcesSearchTriggeredRef,
    articleContext,
    keywords,
    sources,
    contentType,
    toneOfVoice,
    complexity,
    language,
    selectedProject,
    selectedStructure,
    includeImages,
    thumbnailSource,
    knowledgeMode,
    selectedArticleForSummary,
    generatedArticleId,
    articleData,
    setGeneratedArticleId,
    setArticleData,
    setHasInitialArticle,
    setEditorHasContent,
    setShowEditor,
    setForceEditorRefresh,
    setLastSaved,
    setSaveError,
    setIsDraftGenerated,
    setCurrentStep,
    setIsFullScreenLoading,
    setLoaderType,
    setLoaderDetails,
    updateLoaderProgress,
  } = ctx;

  const queueAPI = createQueueAPI({ updateLoaderProgress });

  /**
   * Create new article record for generation (only when generating)
   */
  const createArticleRecord = async () => {
    const contextValue = (articleContextRef.current || articleContext || '').trim();
    const draftTitle = contextValue
      ? (contextValue.length > 50 ? `${contextValue.substring(0, 50)}...` : contextValue)
      : 'Draft Article';

    // Check for existing article from URL first
    const urlArticleId = searchParams.get('id');

    // If we have URL ID, update it with current form values before generation
    if (urlArticleId) {
      log('Using existing article from URL for generation:', urlArticleId);

      const updateData = {
        id: urlArticleId,
        title: draftTitle,
        keywords: keywords || [],
        sources: sources || [],
        content_type: contentType || 'blogpost',
        tone_of_voice: toneOfVoice || 'casual',
        fog_index: complexity || 'fog index: 15',
        language: language || 'polski',
        project: selectedProject || null,
        article_linked_structure: selectedStructure || null,
        status: 'draft',
        article_thumbnail_included: includeImages !== false,
        article_thumbnail_source: includeImages ? thumbnailSource : null,
        article_context: contextValue || null
      };

      const updatedArticle = await saveArticleToDirectus(updateData);
      log('Updated existing URL article before generation:', updatedArticle.data.id);
      return updatedArticle.data;
    }

    // If we already have a generated article, update it
    if (generatedArticleId && articleData) {
      log('Using existing article for generation:', generatedArticleId);

      // Update it with current data before generation
      const updateData = {
        id: generatedArticleId,
        title: draftTitle,
        keywords: keywords || [],
        sources: sources || [],
        content_type: contentType || 'blogpost',
        tone_of_voice: toneOfVoice || 'casual',
        fog_index: complexity || 'fog index: 15',
        language: language || 'polski',
        project: selectedProject || null,
        article_linked_structure: selectedStructure || null,
        status: 'draft',
        article_thumbnail_included: includeImages !== false,
        article_thumbnail_source: includeImages ? thumbnailSource : null,
        article_context: contextValue || null
      };

      const updatedArticle = await saveArticleToDirectus(updateData);
      log('Updated article record for generation:', updatedArticle.data.id);
      return updatedArticle.data;
    }

    // NEW: Create article record ONLY when starting generation
    log('Creating new article record for generation');

    // Prepare sources array based on content type and knowledge mode
    let articleSources = sources || [];
    let finalArticleContext = contextValue || null;

    if (contentType === 'summary') {
      log('📋 SUMMARY: Preparing sources for summary content type');
      log('📋 SUMMARY: Knowledge mode:', knowledgeMode);
      log('📋 SUMMARY: Selected article:', selectedArticleForSummary);

      if (knowledgeMode === 'manual' && selectedArticleForSummary) {
        // Manual mode: Use selected article and mark it as selected for summary
        articleSources = [{
          ...selectedArticleForSummary,
          isSelectedForSummary: true,
          summary_mode: 'selected_article'
        }];
        log('📋 SUMMARY: Manual mode - using selected article:', selectedArticleForSummary.title);
      } else if (knowledgeMode === 'automatic') {
        // Automatic mode: Encode mode in article_context for worker to detect
        finalArticleContext = `AUTOMATIC_ARTICLE_MODE:${contextValue || 'scientific research'}`;
        articleSources = []; // No pre-selected sources, AI will search
        log('📋 SUMMARY: Automatic mode - AI will search for articles');
      } else {
        console.warn('📋 SUMMARY: Invalid state - manual mode without selected article');
      }
    }

    const newArticleData = {
      title: draftTitle,
      keywords: keywords || [],
      sources: articleSources,
      content_type: contentType || 'blogpost',
      tone_of_voice: toneOfVoice || 'casual',
      fog_index: complexity || 'fog index: 15',
      language: language || 'polski',
      project: selectedProject || null,
        article_linked_structure: selectedStructure || null,
        status: 'draft',
        article_thumbnail_included: includeImages !== false,
        article_thumbnail_source: includeImages ? thumbnailSource : null,
        article_context: finalArticleContext,
      article_draft: JSON.stringify({
        time: Date.now(),
        blocks: [],
        version: "2.29.1"
      })
    };

    console.log('📋 CREATE_ARTICLE_RECORD_THUMBNAIL:', {
      includeImages,
      thumbnailSource,
      article_thumbnail_source: newArticleData.article_thumbnail_source,
      willSave: includeImages && thumbnailSource
    });

    const newArticle = await saveArticleToDirectus(newArticleData);
    log('Created new article record:', newArticle.data.id);

    // Update state
    setGeneratedArticleId(newArticle.data.id);
    setArticleData(newArticle.data);
    setHasInitialArticle(true);

    // Update URL with new article ID
    const currentStep = stepIndexToName[getValidStepNumber()];
    const typeParam = contentType ? `&type=${contentType}` : '';
    navigate(`/writer/articles/create-article/step/${currentStep}?id=${newArticle.data.id}${typeParam}`, { replace: true });

    return newArticle.data;
  };

  // ========================= QUEUE GENERATION FUNCTION =========================

  /**
   * Queue-based generation function (fallback when API server not available)
   */
  const autoGenerateWithQueue = async () => {
    // The render-time generatedArticleId is still null for a new article, so
    // the catch below needs the id of the record this attempt created.
    let createdArticleId = null;
    try {
      log('📋 QUEUE SYSTEM: Creating article record in Directus');
      updateLoaderProgress({
        backend: 'queue',
        phase: 'create_article',
        detail: 'Tworzymy rekord artykulu w Directus'
      });

      // Create article record
      const newArticle = await createArticleRecord();
      log('✅ QUEUE SYSTEM: Article created with ID:', newArticle.id);
      createdArticleId = newArticle.id;
      updateLoaderProgress({
        articleId: newArticle.id,
        detail: `Utworzono artykul #${newArticle.id}, dodajemy do kolejki`
      });

      // Set article status to "ongoing" IMMEDIATELY after creation
      try {
        log('🔒 QUEUE SYSTEM: Setting article status to ongoing (IMMEDIATE)');
        await directusAPI.patch(`/items/articles/${newArticle.id}`, {
          status: 'ongoing'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ QUEUE SYSTEM: Article status set to ongoing (IMMEDIATE)');
      } catch (error) {
        log('❌ QUEUE SYSTEM: Error setting immediate ongoing status:', error);
      }

      log('📤 QUEUE SYSTEM: Adding article to queue');
      updateLoaderProgress({
        phase: 'queue_add',
        queueStatus: 'queued',
        detail: 'Dodajemy artykul do kolejki workerow'
      });
      await queueAPI.addToQueue(newArticle);

      log('⏱️ QUEUE SYSTEM: Starting status polling');
      updateLoaderProgress({
        phase: 'queue_poll',
        detail: 'Monitorujemy status draftu w kolejce',
        queueStatus: 'queued'
      });

      const completedArticle = await queueAPI.pollStatus(newArticle.id, (article) => {
        log('📊 QUEUE SYSTEM: Status update:', article.queue_status);
      });

      log('✅ QUEUE SYSTEM: Article generation completed:', completedArticle.id);
      updateLoaderProgress({
        phase: 'render_draft',
        queueStatus: completedArticle.queue_status || 'completed',
        detail: 'Draft gotowy - ladujemy go do edytora'
      });
      log('🔍 QUEUE SYSTEM: Article draft exists:', !!completedArticle.article_draft);

      // Parse and load the generated draft
      let draftContent;
      if (completedArticle.article_draft) {
        try {
          if (typeof completedArticle.article_draft === 'object') {
            draftContent = completedArticle.article_draft;
          } else {
            draftContent = JSON.parse(completedArticle.article_draft);
          }
        } catch (e) {
          log('⚠️ QUEUE SYSTEM: Error parsing draft content, creating fallback:', e);
          draftContent = parseMarkdownToEditorJS(completedArticle.article_draft || 'Draft content generated');
        }
      } else {
        log('⚠️ QUEUE SYSTEM: No draft content found, creating empty structure');
        draftContent = { time: Date.now(), blocks: [], version: "2.29.1" };
      }

      // Instead of creating EditorJS directly, update articleData with new draft
      // This will trigger useEffect in useEditor.js to create EditorJS with the new data
      log('📝 QUEUE SYSTEM: Updating articleData with new draft structure');
      const updatedArticleData = {
        ...completedArticle,
        article_draft: draftContent  // This is the key - new draft structure
      };

      log('📝 QUEUE SYSTEM: Draft loaded with', draftContent.blocks?.length || 0, 'blocks');

      // Clear editorRef so useEffect can create new EditorJS
      log('🗑️ QUEUE SYSTEM: Clearing editorRef to allow useEffect to create new EditorJS');
      if (editorRef.current && editorRef.current.destroy) {
        await editorRef.current.destroy();
      }
      editorRef.current = null;

      // Force useEditor to refresh
      setForceEditorRefresh(prev => !prev);

      // CRITICAL: Use flushSync to force synchronous state updates
      log('⚡ QUEUE SYSTEM: Using flushSync to force synchronous state updates');
      flushSync(() => {
        // Update local state - this will trigger useEffect in useEditor.js
        setGeneratedArticleId(completedArticle.id);
        setArticleData(updatedArticleData);  // This triggers useEffect to create EditorJS
        setLastSaved(new Date().toISOString());
        setSaveError('');
        setIsDraftGenerated(true);

        // Draft generated on step 2 -> keep user on step 2 (preview before keywords)
        setCurrentStep(2);
        setEditorHasContent(true);
        setShowEditor(true);
        setIsDraftGenerated(true);
      });

      log('✅ QUEUE SYSTEM: All state updated synchronously using flushSync');
      log('👁️ QUEUE SYSTEM: Set currentStep=2, editorHasContent=true, showEditor=true');

      // Set article status back to "draft" after successful generation
      try {
        log('🔒 QUEUE DRAFT: Setting article status back to draft');
        await directusAPI.patch(`/items/articles/${completedArticle.id}`, {
          status: 'draft'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ QUEUE DRAFT: Article status set back to draft');
      } catch (error) {
        log('❌ QUEUE DRAFT: Error setting status back to draft:', error);

        // Set article status back to "draft" on error
        try {
          log('🔒 QUEUE DRAFT ERROR: Setting article status back to draft');
          await directusAPI.patch(`/items/articles/${completedArticle.id}`, {
            status: 'draft'
          }, {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getStoredToken()}`
            }
          });
          log('✅ QUEUE DRAFT ERROR: Article status set back to draft');
        } catch (retryError) {
          log('❌ QUEUE DRAFT ERROR RETRY: Final error setting status:', retryError);
        }
      }

      // Re-fetch article to ensure we have latest data
      try {
        log('🔄 QUEUE SYSTEM: Re-fetching article data to ensure state consistency');
        updateLoaderProgress({
          phase: 'sync',
          detail: 'Synchronizujemy dane artykulu po generowaniu'
        });
        const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
        const response = await directusAPI.get(`/items/articles/${completedArticle.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const refreshedArticle = response.data.data;
        log('🔄 QUEUE SYSTEM: Refreshed article data:', refreshedArticle);

        // Update states with refreshed data
        flushSync(() => {
          setArticleData(refreshedArticle);
          setEditorHasContent(true);
          setShowEditor(true);
        });

        log('✅ QUEUE SYSTEM: Article data refreshed and states updated');
      } catch (error) {
        log('❌ QUEUE SYSTEM: Error refreshing article data:', error);
      }

      // Wait for useEffect to create EditorJS
      log('⏳ QUEUE SYSTEM: Waiting for useEffect to create EditorJS...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Debug: Check if element exists and is visible after useEffect
      const elementAfterUseEffect = document.getElementById('editorjs');
      log('🔍 QUEUE SYSTEM: Element exists after useEffect:', !!elementAfterUseEffect);
      if (elementAfterUseEffect) {
        log('🔍 QUEUE SYSTEM: Element styles:', {
          display: elementAfterUseEffect.style.display,
          visibility: elementAfterUseEffect.style.visibility,
          opacity: elementAfterUseEffect.style.opacity,
          position: elementAfterUseEffect.style.position,
          top: elementAfterUseEffect.style.top,
          height: elementAfterUseEffect.style.height,
          minHeight: elementAfterUseEffect.style.minHeight
        });
        log('🔍 QUEUE SYSTEM: Element children count:', elementAfterUseEffect.children.length);
      }

      // Scroll to editor
      setTimeout(() => {
        const draftReviewSection = document.getElementById('draft-review');
        if (draftReviewSection) {
          draftReviewSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
      window.scrollTo(0, 0);

      // Set article status back to "draft" after successful generation
      try {
        log('🔒 QUEUE DRAFT: Setting article status back to draft');
        await directusAPI.patch(`/items/articles/${completedArticle.id}`, {
          status: 'draft'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ QUEUE DRAFT: Article status set back to draft');
      } catch (statusError) {
        log('❌ QUEUE DRAFT: Error setting draft status:', statusError);
      }

    } catch (error) {
      log('❌ QUEUE SYSTEM ERROR:', error);

      // Persist a terminal failure so the article list updates immediately.
      const failedArticleId = createdArticleId || generatedArticleId;
      if (failedArticleId) {
        await markArticleFailed(failedArticleId);
      }

      throw error;
    }
  };

  // ========================= UPDATED GENERATION FUNCTIONS =========================

  const autoGenerateAndRender = async () => {
    log('\n🚀 ============ WORKERS ARTICLE GENERATION START ============');
    sourcesSearchTriggeredRef.current = false;
    setIsFullScreenLoading(true);
    setLoaderType('draft');
    updateLoaderProgress({
      phase: 'healthcheck',
      detail: 'Sprawdzamy dostepnosc workerow',
      backend: '',
      articleId: null,
      queueStatus: '',
      attempt: null,
      totalAttempts: null
    });

    try {
      // Check if API server is available
      log('🔍 WORKERS: Checking API server availability...');
      const useApi = await isApiServerAvailable();

      log('📊 WORKERS: Detection results:', {
        apiAvailable: useApi,
        queueAvailable: queueAPI.isQueueAvailable(),
        isLocalhost: isLocalhost()
      });

      if (useApi) {
        log(`🎯 WORKERS: ✅ Using API Server (${LOCAL_WORKER_API})`);
        updateLoaderProgress({
          backend: 'api',
          phase: 'draft_request',
          detail: 'Laczymy z API draft i rozpoczynamy generowanie'
        });
        try {
          await autoGenerateViaAPI();
        } catch (apiError) {
          log('⚠️ WORKERS: API draft flow failed, trying queue fallback:', apiError?.message);
          if (queueAPI.isQueueAvailable()) {
            updateLoaderProgress({
              backend: 'queue',
              phase: 'queue_fallback',
              detail: 'API nie odpowiedzialo - przelaczamy na kolejke workerow'
            });
            await autoGenerateWithQueue();
          } else {
            throw apiError;
          }
        }
      } else if (queueAPI.isQueueAvailable()) {
        log('🎯 WORKERS: ✅ Using Queue System (background workers)');
        updateLoaderProgress({
          backend: 'queue',
          phase: 'queue_start',
          detail: 'Uruchamiamy generowanie draftu przez kolejke workerow'
        });
        await autoGenerateWithQueue();
      } else {
        log('❌ WORKERS: No generation backend available (API and queue fallback unavailable)');
        throw new Error('Workers system not available. Please check workers API health and queue processing.');
      }
    } catch (error) {
      log('❌ WORKERS ERROR:', error.message);
      log('❌ WORKERS ERROR STACK:', error.stack);
      setSaveError('Workers error: ' + error.message);
      alert('Workers generation failed. Check console for details.');
    } finally {
      log('✅ ============ WORKERS ARTICLE GENERATION END ============\n');
      setIsFullScreenLoading(false);
      setLoaderDetails({
        phase: 'init',
        detail: '',
        backend: '',
        articleId: null,
        queueStatus: '',
        attempt: null,
        totalAttempts: null
      });
    }
  };

  /**
   * New API-based generation function
   */
  async function autoGenerateViaAPI() {
    // The render-time generatedArticleId is still null for a new article, so
    // the catch below needs the id of the record this attempt created.
    let createdArticleId = null;
    try {
      log('📋 API SERVER: Creating article record in Directus');
      updateLoaderProgress({
        backend: 'api',
        phase: 'create_article',
        detail: 'Tworzymy rekord artykulu w Directus'
      });

      // Create article record
      const newArticle = await createArticleRecord();
      log('✅ API SERVER: Article created with ID:', newArticle.id);
      createdArticleId = newArticle.id;
      updateLoaderProgress({
        articleId: newArticle.id,
        detail: `Utworzono artykul #${newArticle.id}, przygotowujemy draft`
      });

      // Set article status to "ongoing" IMMEDIATELY after creation
      try {
        log('🔒 API SERVER: Setting article status to ongoing (IMMEDIATE)');
        await directusAPI.patch(`/items/articles/${newArticle.id}`, {
          status: 'ongoing'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ API SERVER: Article status set to ongoing (IMMEDIATE)');
      } catch (error) {
        log('❌ API SERVER: Error setting immediate ongoing status:', error);
      }

      // Verify article_thumbnail_source was saved by fetching article from Directus
      try {
        const verifyResponse = await directusAPI.get(`/items/articles/${newArticle.id}`, {
          params: {
            fields: 'id,article_thumbnail_source,article_thumbnail_included'
          },
          headers: {
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        const verifiedArticle = verifyResponse.data.data;
        log('🔍 API SERVER: Verified article data:', {
          id: verifiedArticle.id,
          article_thumbnail_source: verifiedArticle.article_thumbnail_source,
          article_thumbnail_included: verifiedArticle.article_thumbnail_included
        });
      } catch (error) {
        log('⚠️ API SERVER: Could not verify article data:', error);
      }

      log('🚀 API SERVER: Generating draft via API');
      updateLoaderProgress({
        phase: 'draft_request',
        detail: 'Generujemy draft: analizujemy kontekst i uruchamiamy narzedzia'
      });

      // Generate draft via API with fallback mechanism
      const requestPayload = {
        articleId: newArticle.id,
        context: (articleContextRef.current || articleContext || '').trim() || 'Article context',
        keywords: keywords || [],
        toneOfVoice: toneOfVoice || 'professional',
        complexity: complexity || 'fog index: 15',
        language: language || 'english',
        contentType: contentType || 'blogpost',
        includeImages: includeImages !== false
      };

      const { result, successfulEndpoint } = await requestDraftFromWorkers(requestPayload, { updateLoaderProgress });

      log(`✅ API SERVER: Draft generated successfully using: ${successfulEndpoint}`);
      updateLoaderProgress({
        phase: 'render_draft',
        detail: 'Draft gotowy - ladujemy go do edytora'
      });

      // DEBUG: Check if element exists at the very beginning
      const elementAtStart = document.getElementById('editorjs');
      log('🔍 API SERVER: Element exists at start:', !!elementAtStart);
      log('🔍 API SERVER: All elements with editorjs class:', document.querySelectorAll('#editorjs').length);
      log('🔍 API SERVER: All div elements:', document.querySelectorAll('div').length);

      // Editor element already exists in JSX - no need to create it dynamically
      log('✅ API SERVER: Using existing editor element from JSX');

      // FIRST: Make editor element visible in DOM
      log('👁️ API SERVER: Making editor element visible');
      setEditorHasContent(true);
      setShowEditor(true);

      // Wait for React to re-render and show the editor element
      log('⏳ API SERVER: Waiting for React to show editor element...');
      await new Promise(resolve => setTimeout(resolve, 500));

      // Instead of creating EditorJS directly, update articleData with new draft
      // This will trigger useEffect in useEditor.js to create EditorJS with the new data
      log('📝 API SERVER: Updating articleData with new draft structure');
      const updatedArticleData = {
        ...newArticle,
        thread_id: result.threadId,
        article_draft: result.draft  // This is the key - new draft structure
      };

      log('📝 API SERVER: New article_draft structure:', JSON.stringify(result.draft, null, 2));

      // Clear editorRef so useEffect can create new EditorJS
      log('🗑️ API SERVER: Clearing editorRef to allow useEffect to create new EditorJS');
      if (editorRef.current && editorRef.current.destroy) {
        await editorRef.current.destroy();
      }
      editorRef.current = null;

      // Force useEditor to refresh
      setForceEditorRefresh(prev => !prev);

      // CRITICAL: Use flushSync to force synchronous state updates
      log('⚡ API SERVER: Using flushSync to force synchronous state updates');
      flushSync(() => {
        // Update local state - this will trigger useEffect in useEditor.js
        setGeneratedArticleId(newArticle.id);
        setArticleData(updatedArticleData);  // This triggers useEffect to create EditorJS
        setLastSaved(new Date().toISOString());
        setSaveError('');
        setIsDraftGenerated(true);

        // Draft generated on step 2 -> keep user on step 2 (preview before keywords)
        setCurrentStep(2);
        setEditorHasContent(true);
        setShowEditor(true);
        setIsDraftGenerated(true);
      });

      log('✅ API SERVER: All state updated synchronously using flushSync');
      log('👁️ API SERVER: Set currentStep=2, editorHasContent=true, showEditor=true');

      // Wait for useEffect to create EditorJS
      log('⏳ API SERVER: Waiting for useEffect to create EditorJS...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Debug: Check if element exists and is visible after useEffect
      const elementAfterUseEffect = document.getElementById('editorjs');
      log('🔍 API SERVER: Element exists after useEffect:', !!elementAfterUseEffect);
      if (elementAfterUseEffect) {
        log('🔍 API SERVER: Element styles:', {
          display: elementAfterUseEffect.style.display,
          visibility: elementAfterUseEffect.style.visibility,
          opacity: elementAfterUseEffect.style.opacity,
          position: elementAfterUseEffect.style.position,
          top: elementAfterUseEffect.style.top,
          height: elementAfterUseEffect.style.height,
          minHeight: elementAfterUseEffect.style.minHeight
        });
        log('🔍 API SERVER: Element children count:', elementAfterUseEffect.children.length);
        log('🔍 API SERVER: Element innerHTML length:', elementAfterUseEffect.innerHTML.length);

        // Force element to be visible (backup measure)
        log('👁️ API SERVER: Ensuring element is visible');
        elementAfterUseEffect.style.display = 'block';
        elementAfterUseEffect.style.visibility = 'visible';
        elementAfterUseEffect.style.opacity = '1';
        elementAfterUseEffect.style.position = 'relative';
        elementAfterUseEffect.style.top = 'auto';
        elementAfterUseEffect.style.minHeight = 'auto';
        elementAfterUseEffect.style.height = 'auto';
        elementAfterUseEffect.style.pointerEvents = 'auto';
        elementAfterUseEffect.style.zIndex = '1';

        log('✅ API SERVER: Element visibility ensured');
      }

      // Scroll to editor
      setTimeout(() => {
        const draftReviewSection = document.getElementById('draft-review');
        if (draftReviewSection) {
          draftReviewSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);

      // Debug: Check states after setting them (only on localhost)
      if (isLocalhost()) {
        setTimeout(() => {
          log('🔍 DEBUG STATES AFTER SETTING:');
          log('- currentStep:', 4); // We set it to 4
          log('- editorHasContent:', true); // We set it to true
          log('- showEditor:', true); // We set it to true

          const element = document.getElementById('editorjs');
          if (element) {
            log('🔍 ELEMENT STYLES:');
            log('- minHeight:', element.style.minHeight);
            log('- opacity:', element.style.opacity);
            log('- visibility:', element.style.visibility);
            log('- position:', element.style.position);
            log('- top:', element.style.top);
            log('- display:', element.style.display);
            log('- pointerEvents:', element.style.pointerEvents);

            log('🔍 COMPUTED STYLES:');
            const computed = window.getComputedStyle(element);
            log('- computed minHeight:', computed.minHeight);
            log('- computed opacity:', computed.opacity);
            log('- computed visibility:', computed.visibility);
            log('- computed position:', computed.position);
            log('- computed display:', computed.display);

            // Check element position and viewport
            const rect = element.getBoundingClientRect();
            log('🔍 ELEMENT POSITION:');
            log('- top:', rect.top);
            log('- left:', rect.left);
            log('- width:', rect.width);
            log('- height:', rect.height);
            log('- bottom:', rect.bottom);
            log('- right:', rect.right);

            log('🔍 VIEWPORT:');
            log('- window.innerHeight:', window.innerHeight);
            log('- window.innerWidth:', window.innerWidth);
            log('- window.scrollY:', window.scrollY);
            log('- window.scrollX:', window.scrollX);

            log('🔍 ELEMENT VISIBILITY CHECK:');
            log('- is in viewport:', rect.top < window.innerHeight && rect.bottom > 0);
            log('- has size:', rect.width > 0 && rect.height > 0);

            // Check parent elements
            log('🔍 PARENT ELEMENTS:');
            let parent = element.parentElement;
            let level = 0;
            while (parent && level < 5) {
              log(`- parent ${level}:`, parent.tagName, parent.className, parent.id);
              const parentRect = parent.getBoundingClientRect();
              log(`  - parent ${level} position:`, parentRect.top, parentRect.left, parentRect.width, parentRect.height);
              parent = parent.parentElement;
              level++;
            }
          } else {
            log('❌ ELEMENT NOT FOUND!');
          }
        }, 2000);
      }

      window.scrollTo(0, 0);

      // Set article status back to "draft" after successful generation
      try {
        log('🔒 API DRAFT: Setting article status back to draft');
        await directusAPI.patch(`/items/articles/${newArticle.id}`, {
          status: 'draft'
        }, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getStoredToken()}`
          }
        });
        log('✅ API DRAFT: Article status set back to draft');
      } catch (statusError) {
        log('❌ API DRAFT: Error setting draft status:', statusError);
      }

      // Re-fetch article to ensure we have latest data
      try {
        log('🔄 API SERVER: Re-fetching article data to ensure state consistency');
        updateLoaderProgress({
          phase: 'sync',
          detail: 'Synchronizujemy dane artykulu po generowaniu'
        });
        const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
        const response = await directusAPI.get(`/items/articles/${newArticle.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const refreshedArticle = response.data.data;
        log('🔄 API SERVER: Refreshed article data:', refreshedArticle);

        // Update states with refreshed data
        flushSync(() => {
          setArticleData(refreshedArticle);
          setEditorHasContent(true);
          setShowEditor(true);
        });

        log('✅ API SERVER: Article data refreshed and states updated');
      } catch (error) {
        log('❌ API SERVER: Error refreshing article data:', error);
      }

    } catch (error) {
      log('❌ API SERVER ERROR:', error);

      // Persist a terminal failure so the article list updates immediately.
      const failedArticleId = createdArticleId || generatedArticleId;
      if (failedArticleId) {
        await markArticleFailed(failedArticleId);
      }

      throw error;
    }
  }

  return {
    queueAPI,
    createArticleRecord,
    autoGenerateAndRender,
    autoGenerateViaAPI,
    autoGenerateWithQueue,
  };
}
